import { errorMap } from "@duet/shared/zod/errorMap";
import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, keyBy } from "lodash";
import { z } from "zod";
import { SystemParametersInput } from "~/gql/generated";
import {
  api,
  SystemParametersFragment,
  type RunningProjectSimQuery,
} from "../../gql/generated";
import { ComponentFieldError } from "../projectDesign/pvComponents/types";
import {
  SystemParameterField,
  systemParametersSchema,
  systemParamsMetadata,
} from "./systemParams";

export type RunningProjectSim = NonNullable<
  RunningProjectSimQuery["projectDuetSim"][number]
>;

type SystemParameterErrors = Partial<
  Record<keyof Omit<SystemParametersInput, "project_id">, ComponentFieldError>
>;

export interface ProjectState {
  simulationInProgress: RunningProjectSim | null;
  systemParameters: SystemParametersFragment | null;
  systemParameterErrors: SystemParameterErrors;
}

const initialState: ProjectState = {
  simulationInProgress: null,
  systemParameters: null,
  systemParameterErrors: {},
};

function validateSystemParameters(systemParameters: SystemParametersFragment) {
  let systemParameterErrors: SystemParameterErrors;

  const result = systemParametersSchema.safeParse(cloneDeep(systemParameters), {
    errorMap,
  });

  if (result.success) {
    systemParameterErrors = {};
  } else {
    const errors = result.error.issues
      .map((issue) => ({
        path: issue.path,
        message: issue.message,
        code: issue.code,
        received:
          "received" in issue ? (issue.received as z.ZodParsedType) : undefined,
        expected:
          "expected" in issue ? (issue.expected as z.ZodParsedType) : undefined,
      }))
      .filter((issue) => {
        const fieldPath = issue.path.join(".");
        const config = systemParamsMetadata[fieldPath as SystemParameterField];
        if (
          config?.disabledIf?.(systemParameters) ||
          config?.hideIf?.(systemParameters)
        ) {
          return false;
        } else {
          return true;
        }
      });
    systemParameterErrors = keyBy(errors, (error) => error.path.join("."));
  }

  return systemParameterErrors;
}

export const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    setSimulationInProgress: (
      state,
      action: PayloadAction<RunningProjectSim | null>
    ) => {
      state.simulationInProgress = action.payload;
    },
    setSystemParameters: (
      state,
      action: PayloadAction<Partial<Omit<SystemParametersInput, "project_id">>>
    ) => {
      if (state.systemParameters) {
        Object.assign(state.systemParameters, action.payload);

        state.systemParameterErrors = validateSystemParameters(
          state.systemParameters
        );
      }
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(api.endpoints.GetProject.matchFulfilled),
      (state, action) => {
        state.systemParameters = action.payload.project.system_parameters;
        state.systemParameterErrors = validateSystemParameters(
          state.systemParameters
        );
      }
    );
    builder.addMatcher(
      isAnyOf(api.endpoints.RunningProjectSim.matchFulfilled),
      (state, action) => {
        state.simulationInProgress = action.payload.projectDuetSim[0] ?? null;
      }
    );
    builder.addMatcher(
      isAnyOf(api.endpoints.StopRunningSimulation.matchFulfilled),
      (state, action) => {
        if (
          state.simulationInProgress?.id === action.meta.arg.originalArgs.id
        ) {
          state.simulationInProgress = null;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(api.endpoints.RunProjectBlockSimulation.matchFulfilled),
      (state, action) => {
        if (
          action.payload.runProjectBlockSimulation.success &&
          action.payload.runProjectBlockSimulation.duetSimulation
        ) {
          state.simulationInProgress =
            action.payload.runProjectBlockSimulation.duetSimulation;
        }
      }
    );
  },
});

export const { setSimulationInProgress, setSystemParameters } =
  projectSlice.actions;
